// Pure diffing logic for the canvas version-diff overlay: matches nodes/edges
// by id, matches slot variants by condition, and does a line-then-word diff
// for the side-by-side view. No React/DOM here -- rendering lives in the
// sibling components.
import { diffArrays } from 'diff';

export const NODE_META_FIELDS = ['type', 'label', 'promptType', 'matchMode'];
export const EDGE_MEANINGFUL_FIELDS = ['source', 'target', 'sourceHandle', 'targetHandle'];
export const EDGE_MEANINGFUL_DATA_FIELDS = ['behavior', 'edgeKind', 'slotName'];

// Long unchanged runs collapse (git-style); short ones stay visible for context.
export const COLLAPSE_MIN_TOKENS = 60;
export const CONTEXT_TOKENS = 16;
export const LINE_COLLAPSE_MIN = 8;
export const LINE_CONTEXT = 2;

export function tokenize(text) {
  return text.match(/\s+|\S+/g) || [];
}

function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => deepEqual(a[k], b[k]));
}

// prompt_slot nodes carry variants/fallback instead of a plain content string.
// `type` here is React Flow's top-level node discriminator, not data.type.
export function isSlotNode(node) {
  if (!node) return false;
  if (node.type === 'prompt_slot') return true;
  return !!node.data && Object.prototype.hasOwnProperty.call(node.data, 'variants');
}

// slot name (as referenced via {{name}}) -> node id, for jump-links. Takes
// multiple node lists so both the live canvas and the fetched snapshot resolve.
export function buildSlotNameMap(...nodeLists) {
  const map = new Map();
  for (const nodes of nodeLists) {
    for (const n of nodes) {
      if (isSlotNode(n) && n.data?.name) map.set(n.data.name, n.id);
    }
  }
  return map;
}

function conditionKey(condition) {
  if (!condition) return 'null';
  const keys = Object.keys(condition).sort();
  return JSON.stringify(keys.map((k) => [k, condition[k]]));
}

// Key each variant by its condition so old/new variants can be matched even if reordered.
export function variantsByCondition(data) {
  const map = new Map();
  for (const v of data?.variants || []) {
    map.set(conditionKey(v.condition), v);
  }
  return map;
}

export function formatCondition(cond) {
  if (!cond) return '(always)';
  const field = cond.field ?? '?';
  const op = cond.operator ?? '?';
  const val = cond.value ?? '?';
  return `${field} ${op} ${JSON.stringify(val)}`;
}

export function nodeContentEqual(oldNode, newNode) {
  const oldSlot = isSlotNode(oldNode);
  const newSlot = isSlotNode(newNode);
  if (oldSlot || newSlot) {
    if (oldSlot !== newSlot) return false;
    const od = oldNode.data || {};
    const nd = newNode.data || {};
    if (od.name !== nd.name) return false;
    if ((od.fallback || '') !== (nd.fallback || '')) return false;
    const ov = variantsByCondition(od);
    const nv = variantsByCondition(nd);
    if (ov.size !== nv.size) return false;
    for (const [key, variant] of ov) {
      if (!nv.has(key) || !deepEqual(nv.get(key), variant)) return false;
    }
    return true;
  }
  return (oldNode.data?.content || '') === (newNode.data?.content || '');
}

export function nodeMetaChanges(oldNode, newNode) {
  const changes = [];
  for (const field of NODE_META_FIELDS) {
    const oldValue = oldNode.data?.[field];
    const newValue = newNode.data?.[field];
    if (oldValue !== newValue) changes.push({ field, oldValue, newValue });
  }
  return changes;
}

export function classifyNode(oldNode, newNode) {
  if (!oldNode) return 'added';
  if (!newNode) return 'removed';
  const metaChanged = nodeMetaChanges(oldNode, newNode).length > 0;
  return nodeContentEqual(oldNode, newNode) && !metaChanged ? 'unchanged' : 'changed';
}

export function edgeMetaChanges(oldEdge, newEdge) {
  const changes = [];
  for (const field of EDGE_MEANINGFUL_FIELDS) {
    const oldValue = oldEdge[field];
    const newValue = newEdge[field];
    if (oldValue !== newValue) changes.push({ field, oldValue, newValue });
  }
  for (const field of EDGE_MEANINGFUL_DATA_FIELDS) {
    const oldValue = oldEdge.data?.[field];
    const newValue = newEdge.data?.[field];
    if (oldValue !== newValue) changes.push({ field, oldValue, newValue });
  }
  return changes;
}

export function classifyEdge(oldEdge, newEdge) {
  if (!oldEdge) return 'added';
  if (!newEdge) return 'removed';
  return edgeMetaChanges(oldEdge, newEdge).length > 0 ? 'changed' : 'unchanged';
}

// First-seen order across old-then-new, deduped -- keeps section ordering stable.
function orderedUnion(oldKeys, newKeys) {
  return [...new Set([...oldKeys, ...newKeys])];
}

// Matches by id, preserving first-seen order across old-then-new.
export function matchById(oldList, newList) {
  const oldMap = new Map(oldList.map((item) => [item.id, item]));
  const newMap = new Map(newList.map((item) => [item.id, item]));
  const ids = orderedUnion(oldList.map((item) => item.id), newList.map((item) => item.id));
  return ids.map((id) => ({ id, oldItem: oldMap.get(id) ?? null, newItem: newMap.get(id) ?? null }));
}

// Diffs a slot node's variants (matched by condition) and fallback.
// Unchanged variants are omitted.
export function diffVariants(oldData, newData) {
  const oldV = variantsByCondition(oldData);
  const newV = variantsByCondition(newData);
  const keys = orderedUnion([...oldV.keys()], [...newV.keys()]);
  const variants = [];
  for (const key of keys) {
    const ov = oldV.get(key);
    const nv = newV.get(key);
    const condition = (ov || nv).condition;
    if (!ov) {
      variants.push({ status: 'added', condition, oldContent: null, newContent: nv.content || '' });
    } else if (!nv) {
      variants.push({ status: 'removed', condition, oldContent: ov.content || '', newContent: null });
    } else if ((ov.content || '') !== (nv.content || '')) {
      variants.push({ status: 'changed', condition, oldContent: ov.content || '', newContent: nv.content || '' });
    }
  }
  return {
    nameChanged: oldData.name !== newData.name,
    oldName: oldData.name || '',
    newName: newData.name || '',
    variants,
    fallbackChanged: (oldData.fallback || '') !== (newData.fallback || ''),
    oldFallback: oldData.fallback || '',
    newFallback: newData.fallback || '',
  };
}

// Thin wrapper over jsdiff's array diff, using our own whitespace-preserving
// tokenizer so word boundaries land between words, not mid-word.
export function diffWordsSegments(oldText, newText) {
  const oldTokens = tokenize(oldText);
  const newTokens = tokenize(newText);
  const changes = diffArrays(oldTokens, newTokens);
  return changes.map((c) => ({
    type: c.added ? 'insert' : c.removed ? 'delete' : 'equal',
    value: c.value.join(''),
  }));
}

// Splits a long unchanged run into head/middle/tail so the UI can show head+tail
// for context and collapse the middle behind a "Show N unchanged words" toggle.
export function splitEqualRunForCollapse(text) {
  const tokens = tokenize(text);
  if (tokens.length <= COLLAPSE_MIN_TOKENS) {
    return { collapsible: false, text };
  }
  const head = tokens.slice(0, CONTEXT_TOKENS).join('');
  const tail = tokens.slice(-CONTEXT_TOKENS).join('');
  const middleTokens = tokens.slice(CONTEXT_TOKENS, tokens.length - CONTEXT_TOKENS);
  return {
    collapsible: true,
    head,
    middle: middleTokens.join(''),
    tail,
    wordCount: middleTokens.filter((t) => t.trim()).length,
  };
}

// Line-level diff for the side-by-side view: a row only appears for a line that
// actually differs. A changed line pair gets word-diffed within its own row
// instead of splitting the paragraph into one row per tiny edit. Long runs of
// unchanged lines collapse to a single {kind:'collapsed'} row.
export function diffLinesForSideBySide(oldContent, newContent) {
  const oldLines = oldContent.split('\n');
  const newLines = newContent.split('\n');
  const changes = diffArrays(oldLines, newLines);
  const rows = [];

  const pushEqualBlock = (block) => {
    if (block.length <= LINE_COLLAPSE_MIN) {
      for (const line of block) rows.push({ kind: 'equal', oldLine: line, newLine: line });
      return;
    }
    const head = block.slice(0, LINE_CONTEXT);
    const tail = block.slice(-LINE_CONTEXT);
    const middle = block.slice(LINE_CONTEXT, block.length - LINE_CONTEXT);
    for (const line of head) rows.push({ kind: 'equal', oldLine: line, newLine: line });
    rows.push({ kind: 'collapsed', lines: middle, count: middle.length });
    for (const line of tail) rows.push({ kind: 'equal', oldLine: line, newLine: line });
  };

  const pushReplaceBlock = (oldBlock, newBlock) => {
    const paired = Math.min(oldBlock.length, newBlock.length);
    for (let k = 0; k < paired; k++) {
      rows.push({
        kind: 'changed',
        oldLine: oldBlock[k],
        newLine: newBlock[k],
        wordDiff: diffWordsSegments(oldBlock[k], newBlock[k]),
      });
    }
    for (let k = paired; k < oldBlock.length; k++) rows.push({ kind: 'removed', oldLine: oldBlock[k], newLine: null });
    for (let k = paired; k < newBlock.length; k++) rows.push({ kind: 'added', oldLine: null, newLine: newBlock[k] });
  };

  let i = 0;
  while (i < changes.length) {
    const c = changes[i];
    if (!c.added && !c.removed) {
      pushEqualBlock(c.value);
      i += 1;
    } else if (c.removed && changes[i + 1]?.added) {
      pushReplaceBlock(c.value, changes[i + 1].value);
      i += 2;
    } else if (c.removed) {
      for (const line of c.value) rows.push({ kind: 'removed', oldLine: line, newLine: null });
      i += 1;
    } else {
      for (const line of c.value) rows.push({ kind: 'added', oldLine: null, newLine: line });
      i += 1;
    }
  }
  return rows;
}

// Top-level entry point: diffs two canvases' nodes and edges by id.
// Metadata (name/description/tools/...) is intentionally not handled here --
// AgentVersion snapshots only canvas_id, so there's no historical metadata to diff.
export function diffCanvases(oldCanvas, newCanvas) {
  const nodeMatches = matchById(oldCanvas.nodes || [], newCanvas.nodes || []);
  const nodes = nodeMatches.map(({ id, oldItem, newItem }) => ({
    id,
    oldNode: oldItem,
    newNode: newItem,
    status: classifyNode(oldItem, newItem),
  }));

  const edgeMatches = matchById(oldCanvas.edges || [], newCanvas.edges || []);
  const edges = edgeMatches.map(({ id, oldItem, newItem }) => ({
    id,
    oldEdge: oldItem,
    newEdge: newItem,
    status: classifyEdge(oldItem, newItem),
  }));

  return { nodes, edges };
}
